import React, { useEffect, useState, useCallback } from "react";
import { Pagination, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import pic from "../images/pic.jpg";
import "../styles/home.css";
import api from "./api";
import debounce from "lodash/debounce";
import socialmedia from "../images/social-media.png";
import magnify from "../images/magnifying-glass.png";
import bell from "../images/bell.png";
import award from "../images/award.png";
import box from "../images/box.png";
import hello from "../images/hello.png";
import lockIcon from "../images/padlock.png";

const Home = () => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    full_name: "",
    unread_messages_count: 0,
    total_stars: 0,
    last_star: 0,
    last_star_reason: "",
  });
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Stateهای مربوط به جستجو و دوره‌ها
  const [searchResults, setSearchResults] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(6);
  const [countPages, setCountPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packagesError, setPackagesError] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // State برای مدیریت وضعیت لودینگ قلب‌ها
  const [wishlistLoading, setWishlistLoading] = useState({});

  // State برای پاپ‌آپ پیش‌نمایش
  const [previewPopup, setPreviewPopup] = useState({
    show: false,
    title: "",
  });

  // ========== 1. دریافت اطلاعات پروفایل کاربر ==========
  useEffect(() => {
    const fetchProfile = async () => {
      setLoadingProfile(true);
      try {
        const response = await api.get("/pre_student/api/v3/pre-student-profile/");
        if (response.data) {
          setProfile({
            full_name: response.data.full_name || "",
            unread_messages_count: response.data.unread_messages_count || 0,
            star: response.data.star || 0,
            last_star: response.data.last_star || 0,
            last_star_reason: response.data.last_star_reason || "",
          });
        }
      } catch (err) {
        console.error("خطا در دریافت پروفایل:", err);
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, []);

  // ========== 2. دریافت لیست همه دوره‌ها (packages-list) ==========
  const fetchAllPackages = useCallback(
    async (pageNum) => {
      setLoadingPackages(true);
      setPackagesError(null);

      try {
        const response = await api.get("/pre_student/api/v3/packages-list/", {
          params: {
            page: pageNum,
            page_size: pageSize,
          },
        });

        const data = response.data;
        setAllPackages(data.results || []);
        setTotalCount(data.count || 0);
        const totalPages = Math.ceil((data.count || 0) / pageSize) || 1;
        setCountPages(totalPages);
        setPage(pageNum);
      } catch (err) {
        console.error("خطا در دریافت لیست دوره‌ها:", err);
        setPackagesError("مشکلی در دریافت لیست دوره‌ها پیش آمده است");
        setAllPackages([]);
        setCountPages(1);
      } finally {
        setLoadingPackages(false);
      }
    },
    [pageSize]
  );

  // بارگذاری اولیه
  useEffect(() => {
    fetchAllPackages(1);
  }, [fetchAllPackages]);

  // ========== 3. تابع جستجوی دوره‌ها ==========
  const fetchSearchResults = useCallback(
    async (query, pageNum) => {
      setLoadingPackages(true);
      setPackagesError(null);

      try {
        const searchTerm = query.trim() === "" ? "" : query;

        const response = await api.get("/pre_student/api/v3/search/", {
          params: {
            q: searchTerm,
            page: pageNum,
            page_size: pageSize,
          },
        });

        const data = response.data;

        let packagesArray = [];

        if (data.results) {
          if (typeof data.results === "object" && data.results.packages) {
            packagesArray = data.results.packages;
          } else if (Array.isArray(data.results)) {
            data.results.forEach((keywordGroup) => {
              if (keywordGroup.packages && Array.isArray(keywordGroup.packages)) {
                packagesArray = [...packagesArray, ...keywordGroup.packages];
              }
            });
          }
        }

        setSearchResults(packagesArray);
        setTotalCount(data.count || 0);
        const totalPages = Math.ceil((data.count || 0) / pageSize) || 1;
        setCountPages(totalPages);
        setPage(pageNum);
      } catch (err) {
        console.error("خطا در جستجوی دوره‌ها:", err);
        setPackagesError("مشکلی در جستجوی دوره‌ها پیش آمده است");
        setSearchResults([]);
        setCountPages(1);
      } finally {
        setLoadingPackages(false);
      }
    },
    [pageSize]
  );

  // جستجو با debounce
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.trim() !== "") {
        setIsSearchActive(true);
        fetchSearchResults(query, 1);
      } else {
        setIsSearchActive(false);
        fetchAllPackages(1);
      }
    }, 500),
    [fetchSearchResults, fetchAllPackages]
  );

  useEffect(() => {
    if (searchQuery.trim() !== "") {
      debouncedSearch(searchQuery);
    } else {
      debouncedSearch.cancel();
      setIsSearchActive(false);
      fetchAllPackages(1);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch, fetchAllPackages]);

  // تغییر صفحه
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > countPages) return;

    if (isSearchActive) {
      fetchSearchResults(searchQuery, newPage);
    } else {
      fetchAllPackages(newPage);
    }
  };

  // ========== 4. تابع اضافه کردن به علاقه‌مندی‌ها ==========
  const addToWishlist = async (packageId, event) => {
    event.stopPropagation();

    setWishlistLoading(prev => ({ ...prev, [packageId]: true }));

    try {
      const response = await api.post(`/pre_student/api/v3/wishlist/add/${packageId}/`);

      if (response.data && response.data.message) {
        console.log(response.data.message);
        alert(response.data.message);
      }
    } catch (err) {
      console.error("خطا در افزودن به علاقه‌مندی‌ها:", err);
      if (err.response && err.response.data && err.response.data.message) {
        alert(err.response.data.message);
      } else {
        alert("مشکلی در افزودن به علاقه‌مندی‌ها پیش آمد");
      }
    } finally {
      setWishlistLoading(prev => ({ ...prev, [packageId]: false }));
    }
  };

  // ========== 5. هدایت به صفحه جزئیات دوره یا نمایش پاپ‌آپ پیش‌نمایش ==========
  const goToCourseDetail = (course) => {
    if (course.is_preview) {
      setPreviewPopup({
        show: true,
        title: course.title,
      });
    } else {
      navigate(`/course-detail/${course.id}`);
    }
  };

  // بستن پاپ‌آپ
  const closePreviewPopup = () => {
    setPreviewPopup({
      show: false,
      title: "",
    });
  };

  // رندر پیجینیشن
  const renderPaginationItems = () => {
    let items = [];
    let maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(countPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item key={number} active={number === page} onClick={() => handlePageChange(number)}>
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  const displayPackages = isSearchActive ? searchResults : allPackages;

  if (loadingProfile) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // ========== تابع نمایش قیمت - دقیقاً طبق توضیحات شما ==========
  const renderPrice = (course) => {
    const price = parseInt(course.price) || 0;
    const priceWithoutDiscount = parseInt(course.price_without_discount) || 0;
    
    // حالت اول: قیمت صفر است و قیمت بدون تخفیف دارد
    if (price === 0 && priceWithoutDiscount > 0) {
      return (
        <span>
          <div style={{ textDecoration: "line-through", color: "#999", fontSize: "0.8rem", marginLeft: "6px" , display:"inline"}}>
            {priceWithoutDiscount.toLocaleString()} تومان
          </div>
          <div style={{ color: "#2D9C4B", fontWeight: "bold", fontSize: "1rem" , display:"inline" }}>
            رایگان
          </div>
        </span>
      );
    }
    
    // حالت دوم: قیمت بدون تخفیف صفر است - فقط قیمت رو بنویس
    if (priceWithoutDiscount === 0 && price > 0) {
      return <span style={{ color: "#0961F5", fontWeight: "bold" }}>{price.toLocaleString()} تومان</span>;
    }
    
    // حالت سوم: هر دو صفر هستند - فقط رایگان
    if (price === 0 && priceWithoutDiscount === 0) {
      return <span style={{ color: "#2D9C4B", fontWeight: "bold" }}>رایگان</span>;
    }
    
    // حالت چهارم: هر دو قیمت دارند - روی قیمت بدون تخفیف خط بزن، قیمت رو بنویس
    if (price > 0 && priceWithoutDiscount > 0) {
      return (
        <span>
          <div style={{ textDecoration: "line-through", color: "#999", fontSize: "0.8rem", marginLeft: "6px"  , display:"inline"}}>
            {priceWithoutDiscount.toLocaleString()} تومان
          </div>
          <div style={{ color: "#0961F5", fontWeight: "bold", fontSize: "1rem"  , display:"inline"}}>
            {price.toLocaleString()} تومان
          </div>
        </span>
      );
    }
    
    // fallback
    return <span style={{ color: "#0961F5", fontWeight: "bold" }}>{price.toLocaleString()} تومان</span>;
  };

  return (
    <div className="ms-lg-2 p-3 pt-lg-0 pt-3 pe-0 pe-lg-4">
      {/* پاپ‌آپ پیش‌نمایش */}
      {previewPopup.show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={closePreviewPopup}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "40px",
              borderRadius: "16px",
              maxWidth: "500px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closePreviewPopup}
              style={{
                position: "absolute",
                top: "12px",
                right: "16px",
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#999",
              }}
            >
              ×
            </button>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
            <h4 style={{ marginBottom: "12px", color: "#333" }}>
              دوره "{previewPopup.title}"
            </h4>
            <p style={{ color: "#666", fontSize: "16px", lineHeight: "1.8" }}>
              این دوره به زودی منتشر می‌شود. برای اطلاع از زمان انتشار، منتظر باشید!
            </p>
            <p style={{ color: "#444", fontSize: "15px", lineHeight: "1.8", marginTop: "12px" }}>
              برای اطلاعات بیشتر درباره دوره‌های دیگر و ثبت نام در دوره های آموزشی ، با <strong>خانوم شهری</strong> تماس بگیرید:
            </p>
            <p>    ۰۹۱۵۵۷۴۶۹۵۹</p>
          
            <button
              onClick={closePreviewPopup}
              style={{
                backgroundColor: "#2D9C4B",
                color: "white",
                border: "none",
                padding: "10px 30px",
                borderRadius: "8px",
                fontSize: "16px",
                cursor: "pointer",
                marginTop: "20px",
                transition: "background-color 0.3s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#248a3e")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2D9C4B")}
            >
              متوجه شدم
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="d-flex w-100 d-lg-none justify-content-center">
          <div className="pb-4 d-flex justify-content-between" style={{ borderBottom: "1px solid #e1e1e1", width: "100%" }}>
            <div>
              <h3>سلام {profile.full_name || ""}</h3>
              امروز دوست داری چی یاد بگیری؟
            </div>
            <Link style={{ width: "30%" }} to="../message">
              <span style={{ position: "relative", marginRight: "40px" }}>
                {profile.unread_messages_count > 0 && (
                  <span className="message-number">{profile.unread_messages_count}</span>
                )}
                <img src={bell} width={60} className="mx-3" alt="notification" />
              </span>
            </Link>
          </div>
        </div>

        <div className="d-none w-100 d-lg-flex mb-4 justify-content-center">
          <div className="pb-4 d-flex w-75 justify-content-between" style={{ borderBottom: "1px solid #e1e1e1" }}>
            <div>
              <div>
                <h3>
                  <img src={hello} width={70} className="mx-2" alt="hello" />
                  سلام {profile.full_name || ""}
                </h3>
                <span className="me-5 pe-5"> امروز دوست داری چی یاد بگیری؟</span>
              </div>
            </div>
            <div>
              <span style={{ position: "relative", marginRight: "40px" }}>
                {profile.unread_messages_count > 0 && (
                  <span className="message-number">{profile.unread_messages_count}</span>
                )}
                <img src={bell} width={60} className="mx-3" alt="notification" />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* بخش ستاره‌ها */}
      <div className="row">
        <div className="col-lg-4 col-12 justify-content-center">
          <div className="d-lg-flex justify-content-center d-block bb-none">
            <img src={award} width={60} className="mx-3" alt="award" />
            <p className="d-lg-inline d-flex justify-content-center" style={{ color: "#707070" }}>
              <div className="mt-0 mt-lg-4">ستاره‌هایی که تا امروز گرفتی : </div>
            </p>
            <div className="d-lg-inline pb-3 d-flex justify-content-center">
              <div className="mt-0 mt-lg-4">
                {profile.star} ستاره
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-8 col-12">
          <div className="d-flex pt-3 pt-lg-0 justify-content-center mb-5 mb-lg-0">
            <img src={box} width={60} className="mx-3" alt="star box" />
            <span className="mt-4">
              {profile.last_star > 0
                ? `${profile.last_star} ستاره از مدرست برای ${profile.last_star_reason || "تمرین"} گرفتی`
                : "هنوز ستاره‌ای دریافت نکرده‌ای"}
            </span>
          </div>
        </div>
      </div>

      {/* باکس جستجو */}
      <div className="d-flex mx-lg-5 my-lg-3 mx-0">
        <div className="d-flex justify-content-center w-100">
          <div style={{ position: "relative" }} className="d-block d-lg-none w-100">
            <img src={socialmedia} width={60} className="mx-3" alt="socialmedia" style={{ float: "left" }} />
            <input
              className="course-search w-100"
              type="text"
              placeholder="جستجو کنید..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="course-btn-search" style={{ top: "70px" }}>
              <img src={magnify} width={34} alt="magnify" />
            </button>
          </div>

          <div style={{ position: "relative" }} className="d-none d-lg-flex justify-content-between btn-search-dsk p-4 w-100">
            <div className="d-flex align-items-center" style={{ color: "white" }}>
              <span>برای پیدا کردن دوره‌هات، اینجا جستجو کن</span>
            </div>
            <div>
              <input
                className="course-search course-search1"
                type="text"
                placeholder="جستجو کنید..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="course-btn-search1">
                <img src={magnify} width={34} alt="magnify" />
              </button>
              <img src={socialmedia} width={60} className="mx-3" alt="socialmedia" style={{ position: "absolute", left: "10px", top: "-30px" }} />
            </div>
          </div>
        </div>
      </div>

      {/* لیست دوره‌ها */}
      <div className="all-course-container px-lg-5">
        <h5 className="my-4">
          {isSearchActive ? "نتایج جستجو" : "دوره های ما"}
          {loadingPackages && <Spinner animation="border" size="sm" className="me-2" />}
          {!loadingPackages && displayPackages.length === 0 && (
            <span className="text-muted me-2">{isSearchActive ? "نتیجه‌ای یافت نشد" : "هیچ دوره‌ای یافت نشد"}</span>
          )}
          {isSearchActive && searchQuery && !loadingPackages && displayPackages.length > 0 && (
            <span className="text-muted me-2 fs-6">
              ({totalCount} نتیجه برای "{searchQuery}")
            </span>
          )}
        </h5>

        {packagesError && <div className="alert alert-danger text-center">{packagesError}</div>}

        <div className="row">
          {displayPackages.map((course, index) => (
            <div className="col-12 col-lg-4 mb-4" key={course.id || index}>
              <div
                className="course-card"
                style={{
                  position: "relative",
                  cursor: course.is_preview ? "pointer" : "pointer",
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  opacity: course.is_preview ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!course.is_preview) {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                }}
                onClick={() => goToCourseDetail(course)}
              >
                <div className="all-pic" style={{ position: "relative" }}>
                  {course.logo_url ? (
                    <img className="img23" src={`${course.logo_url}`} alt={course.title} />
                  ) : (
                    <img src={pic} alt={course.title} />
                  )}
                  {/* آیکون قفل برای دوره‌های preview */}
                  {course.is_preview && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.4)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "12px 12px 0 0",
                      }}
                    >
                      <div
                        style={{
                          borderRadius: "50%",
                          padding: "0px",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                        }}
                      >
                       <img src={lockIcon} alt="lockIcon" width={70} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="not-buy p-4">
                  {/* هدر دوره با تایتل و دکمه قلب (فقط برای دوره‌های غیر preview) */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="course-title" style={{ fontWeight: "bold", fontSize: "1.1rem", flex: 1 }}>
                      {course.title}
                    </div>

                    {/* دکمه قلب - فقط برای دوره‌های غیر preview */}
                    {!course.is_preview && (
                      <button
                        onClick={(e) => addToWishlist(course.id, e)}
                        disabled={wishlistLoading[course.id]}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          transition: "all 0.3s ease",
                          marginRight: "4px",
                          padding: "4px",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#fef0f0";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        {wishlistLoading[course.id] ? (
                          <Spinner animation="border" size="sm" style={{ width: "20px", height: "20px", color: "#e74c3c" }} />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ transition: "all 0.3s ease" }}
                          >
                            <path
                              d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
                              fill="none"
                              stroke="#e74c3c"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="row mt-2">
                    <div className="col-6" style={{ color: "#666", fontSize: "0.85rem" }}>
                      مدرس: {course.teacher_name || "نامشخص"}
                    </div>
                    <div className="col-6" style={{ color: "#666", fontSize: "0.85rem" }}>
                      {course.duration_hours || 0} ساعت {course.duration_minutes || 0} دقیقه
                    </div>
                  </div>

                  {/* ردیف ستاره‌ها */}
                  <div className="row mt-2">
                    <div className="col-12" style={{ color: "#f39c12" }}>
                      <svg width="16" height="16" viewBox="0 0 27 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline", marginLeft: "4px" }}>
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M14.8236 0.850248L17.83 6.97067C18.0513 7.414 18.4725 7.7246 18.9626 7.79849L25.7084 8.79325C26.1026 8.85208 26.4617 9.06006 26.6993 9.38025C26.9423 9.7018 27.0422 10.1027 26.9841 10.4982C26.9369 10.8238 26.783 11.1289 26.5521 11.3588L21.6625 16.1588C21.3048 16.5009 21.1414 16.9921 21.2251 17.4779L22.4334 24.2345C22.5643 25.0418 22.0324 25.8054 21.2251 25.9709C20.8984 26.0243 20.561 25.9709 20.2653 25.8163L14.2431 22.6364C13.7949 22.4065 13.2684 22.4065 12.8256 22.6364L6.80336 25.8163C6.05817 26.2172 5.13209 25.9394 4.72979 25.2019C4.5786 24.9077 4.52595 24.5711 4.5732 24.2455L5.77873 17.4888C5.86512 17.0085 5.70178 16.5105 5.34268 16.1684L0.453075 11.3739C0.16958 11.0906 0.00488281 10.7061 0.00488281 10.3052C0.00488281 9.90431 0.16958 9.51981 0.453075 9.23658C0.68527 9.00123 0.990365 8.84661 1.32381 8.80419L8.06963 7.80944C8.55832 7.73418 8.97952 7.43042 9.19686 6.98161L12.102 0.850248C12.3545 0.331661 12.8877 0.000532139 13.4736 0.0114786H13.6531C14.1594 0.0757888 14.6022 0.390498 14.8236 0.850248Z"
                          fill="#FCCB40"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M13.5103 22.4546C13.2457 22.4546 12.9987 22.5449 12.7611 22.6625L6.76043 25.8329C6.03819 26.201 5.12561 25.9068 4.73007 25.2021C4.57752 24.9134 4.51947 24.5823 4.57212 24.2552L5.779 17.5163C5.85865 17.0238 5.6953 16.5325 5.3416 16.185L0.442546 11.385C-0.147393 10.798 -0.147393 9.84017 0.442546 9.2477C0.674742 9.01782 0.975786 8.86731 1.29708 8.81942L8.06855 7.80961C8.58424 7.73435 8.95954 7.44154 9.19578 6.98179L12.1387 0.780639C12.3601 0.278472 13.1148 -0.0690766 13.5211 0.0116532C13.5103 0.423512 13.5103 22.1823 13.5103 22.4546Z"
                          fill="#FAC025"
                        />
                      </svg>
                      {course.total_points} ستاره
                    </div>
                  </div>

                  {/* ردیف قیمت - با تابع renderPrice */}
                  <div className="row mt-2">
                    <div className="col-12" style={{ fontWeight: "500" }}>
                      {renderPrice(course)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* پیجینیشن */}
        {countPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination className="custom-pagination">
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="30" viewBox="0 0 24 24" fill="none">
                    <path d="M8 4L16 12L8 20" stroke="#2D9C4B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  قبلی
                </span>
              </Pagination.Prev>
              {renderPaginationItems()}
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === countPages}>
                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  بعدی
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="30" viewBox="0 0 24 24" fill="none">
                    <path d="M16 4L8 12L16 20" stroke="#2D9C4B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </Pagination.Next>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;